<script setup>

    import { ref, watch, computed } from 'vue'
    import Modal from '@/Components/App/Modal.vue'
    import { util } from '@/Helpers/util'

    const props = defineProps({
        show: { type: Boolean, default: false },
        document: { type: Object, default: null },
        bundle: { type: Object, default: null },
        tags: { type: Array, default: () => [] },
        initialTags: { type: Array, default: () => [] },
        saveLoading: { type: Boolean, default: false }
    })

    const emit = defineEmits(['close', 'submit'])

    const selected = ref([])

    const options = computed(() => {
        return (props.tags || []).map(d => {
            const name = d.name || d.label || d.id || String(d)
            return { id: name, name }
        })
    })

    const original = ref([])

    watch(() => props.document, (doc) => {
        const existingTags = doc && Array.isArray(doc.tags) ? doc.tags : []
        selected.value = existingTags.length > 0
            ? existingTags.slice()
            : (props.initialTags?.length > 0 ? props.initialTags.slice() : [])
        original.value = existingTags.slice()
    }, { immediate: true })

    // when parent finishes saving (saveLoading true -> false), consider current selection saved
    watch(() => props.saveLoading, (now, prev) => {
        if (prev === true && now === false) {
                // Only consider the selection saved if the parent document's tags
                // match the current selection (i.e. the server applied the change).
                const docTags = Array.isArray(props.document?.tags) ? props.document.tags : []
                if (util.arraysHaveSameUniqueElements(docTags, selected.value)) {
                original.value = Array.isArray(selected.value) ? selected.value.slice() : []
                close()
            }
        }
    })

    const hasChanged = computed(() => {
        return !util.arraysHaveSameUniqueElements(selected.value || [], original.value || [])
    })

    function close() {
        emit('close')
    }

    function submit() {
        emit('submit', (selected.value || []).slice())
    }

</script>

<template>
    <Modal :show="props.show" maxWidth="lg" @close="close" :paddingEnable="false">
        <div class="relative">

            <div class="mb-3 rounded-t-md overflow-hidden">
                <div class="flex items-center gap-3 bg-lifeworx-blue-700 text-white px-4 py-3">
                    <svg class="h-6 w-6 flex-shrink-0" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 2h7l5 5v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                        <path d="M13 2v6h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    <h3 class="text-sm font-bold">Tag Document Contents</h3>
                </div>
            </div>

            <div>
                <label class="text-xs font-semibold text-stone-400 block mb-2 px-6">Select all that apply</label>

                <div class="max-h-[500px] overflow-y-auto bg-white border-t border-stone-200 divide-y divide-stone-200">
                    <template v-for="opt in options" :key="opt.id">
                        <label class="flex items-center justify-between px-10 py-1 cursor-pointer hover:bg-lifeworx-blue-50 hover:bg-opacity-10 odd:bg-stone-50">
                            <div class="text-sm font-semibold">{{ opt.name }}</div>
                            <input
                                type="checkbox"
                                :value="opt.id"
                                v-model="selected"
                                class="cursor-pointer m-2 rounded-sm focus:ring-0 border-stone-400"
                            />
                        </label>
                    </template>
                </div>
            </div>

            <div class="py-4 px-6 border-t flex items-center justify-between">
                <div class="text-sm">Select all that apply</div>
                <div class="flex items-center gap-3">
                    <button
                        type="button"
                        class="px-4 py-2 rounded-md border border-lifeworx-orange-500 text-sm font-semibold text-lifeworx-orange-500 bg-white hover:bg-lifeworx-orange-50"
                        @click="close"
                    >
                        Close
                    </button>

                    <button
                        type="button"
                        class="px-4 py-2 rounded-md bg-lifeworx-blue-600 text-white hover:bg-lifeworx-blue-700 disabled:opacity-60"
                        :disabled="props.saveLoading || !hasChanged"
                        @click="submit"
                    >
                        <span v-if="props.saveLoading">Saving…</span>
                        <span v-else>Save</span>
                    </button>
                </div>
            </div>
        </div>
    </Modal>
</template>
